import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import FormRenderer from '../shared/FormRenderer';
import '../superadmin/DistrictsList.css';
import './EditFormInstance.css';

const EditFormInstance = ({ formInstance, onClose, onSave }) => {
  const [formData, setFormData] = useState(null);
  const [fieldOverrides, setFieldOverrides] = useState({});
  const [formValues, setFormValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFormData();
  }, [formInstance]);

  const fetchFormData = async () => {
    try {
      setLoading(true);
      
      // Fetch form instance details
      const instanceResponse = await axios.get(`/api/form-instances/${formInstance.id}`);
      let instanceData = instanceResponse.data;
      
      // Parse form_data if needed
      let parsedFormData = instanceData.form_data;
      if (typeof parsedFormData === 'string') {
        parsedFormData = JSON.parse(parsedFormData);
      }
      
      setFormData(parsedFormData);

      // Fetch existing field overrides
      const overridesResponse = await axios.get(`/api/form-instance-overrides/form-instance/${formInstance.id}`);
      setFieldOverrides(overridesResponse.data || {});

      // Initialize form values (empty for preview)
      const initialValues = {};
      if (parsedFormData && parsedFormData.sections) {
        parsedFormData.sections.forEach(section => {
          if (section.fields) {
            section.fields.forEach(field => {
              if (field.type === 'checkbox') {
                initialValues[field.name] = [];
              } else {
                initialValues[field.name] = '';
              }
            });
          }
        });
      }
      setFormValues(initialValues);
    } catch (error) {
      console.error('Failed to fetch form data:', error);
      setError('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRequired = (fieldName) => {
    setFieldOverrides(prev => {
      const current = prev[fieldName] || {};
      const newRequired = current.is_required === true ? null : (current.is_required === null ? true : true);
      
      return {
        ...prev,
        [fieldName]: {
          ...current,
          is_required: newRequired
        }
      };
    });
  };

  const handleToggleHidden = (fieldName) => {
    setFieldOverrides(prev => {
      const current = prev[fieldName] || {};
      const newHidden = !current.is_hidden;
      
      return {
        ...prev,
        [fieldName]: {
          ...current,
          is_hidden: newHidden
        }
      };
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      await axios.put(`/api/form-instance-overrides/form-instance/${formInstance.id}`, {
        field_overrides: fieldOverrides
      });

      if (onSave) {
        onSave();
      }
      onClose();
    } catch (error) {
      console.error('Failed to save field overrides:', error);
      setError(error.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const getFieldRequired = (field) => {
    const override = fieldOverrides[field.name];
    if (override && override.is_required !== null && override.is_required !== undefined) {
      return override.is_required;
    }
    return field.required || false;
  };

  const getFieldHidden = (field) => {
    const override = fieldOverrides[field.name];
    return override && override.is_hidden === true;
  };

  const applyOverridesToFormData = (formData) => {
    if (!formData || !formData.sections) return formData;

    const modifiedSections = formData.sections.map(section => {
      if (!section.fields) return section;

      const modifiedFields = section.fields.map(field => {
        const override = fieldOverrides[field.name];
        const isHidden = override && override.is_hidden === true;
        
        // Skip hidden fields
        if (isHidden) return null;

        const isRequired = override && override.is_required !== null && override.is_required !== undefined
          ? override.is_required
          : field.required;

        return {
          ...field,
          required: isRequired
        };
      }).filter(field => field !== null);

      return {
        ...section,
        fields: modifiedFields
      };
    });

    return {
      ...formData,
      sections: modifiedSections
    };
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal edit-form-instance-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Edit Form</h3>
            <button onClick={onClose} className="btn-close">&times;</button>
          </div>
          <div className="modal-body">
            <div>Loading form...</div>
          </div>
        </div>
      </div>
    );
  }

  const modifiedFormData = applyOverridesToFormData(formData);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal edit-form-instance-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Edit Form: {formInstance.instance_name || formInstance.master_form_name}</h3>
          <button onClick={onClose} className="btn-close">&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          
          <div className="edit-form-instance-info">
            <p><strong>Note:</strong> You can mark fields as required or hidden for your district. You cannot add or delete fields.</p>
          </div>

          {modifiedFormData && modifiedFormData.sections && (
            <div className="form-instance-edit">
              {modifiedFormData.sections.map((section, sectionIndex) => {
                if (section.admin_only) return null;

                return (
                  <div key={sectionIndex} className="form-section-edit">
                    {section.section_name && (
                      <h4 className="section-title">{section.section_name}</h4>
                    )}
                    {section.section_note && (
                      <p className="section-note">{section.section_note}</p>
                    )}
                    
                    {section.fields && section.fields.map((field, fieldIndex) => {
                      if (field.admin_only) return null;
                      
                      const override = fieldOverrides[field.name] || {};
                      const isRequired = getFieldRequired(field);
                      const isHidden = getFieldHidden(field);
                      const originalRequired = field.required || false;

                      return (
                        <div key={fieldIndex} className="field-edit-container">
                          <div className="field-controls">
                            <div className="field-control-group">
                              <label className="field-control-label">
                                <input
                                  type="checkbox"
                                  checked={isRequired}
                                  onChange={() => handleToggleRequired(field.name)}
                                  disabled={isHidden}
                                />
                                <span>Required</span>
                                {originalRequired !== isRequired && (
                                  <span className="override-indicator">(overridden)</span>
                                )}
                              </label>
                            </div>
                            <div className="field-control-group">
                              <label className="field-control-label">
                                <input
                                  type="checkbox"
                                  checked={isHidden}
                                  onChange={() => handleToggleHidden(field.name)}
                                />
                                <span>Hidden</span>
                              </label>
                            </div>
                          </div>
                          
                          {!isHidden && (
                            <div className="field-preview">
                              <FormRenderer
                                formData={{
                                  sections: [{
                                    section_name: section.section_name,
                                    section_note: section.section_note,
                                    fields: [{
                                      ...field,
                                      required: isRequired
                                    }]
                                  }]
                                }}
                                formValues={formValues}
                                readOnly={true}
                              />
                            </div>
                          )}
                          
                          {isHidden && (
                            <div className="field-hidden-notice">
                              <em>This field is hidden for parents in your district.</em>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button onClick={handleSave} className="btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button onClick={onClose} className="btn-secondary">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default EditFormInstance;

